/**
 * Verified facts for the San Diego Police Department RFI briefing.
 *
 * ACCURACY RULE FOR THIS FILE: every number carries its source. This page is
 * submitted alongside a response to a public-agency solicitation and becomes a
 * public record under the California Public Records Act. Nothing goes in here
 * that cannot be traced to a citation below.
 *
 * If you add a figure, add its source in the same object.
 */

// ===================================================================
// The solicitation itself
// ===================================================================

export const RFI = {
  number: "30000072-27-E",
  title: "Artificial Intelligence (AI) Police Report Writing Program",
  issuedDate: "July 31, 2026",
  questionsDue: "August 12, 2026, 12:00 p.m.",
  closingDate: "September 17, 2026, 2:00 p.m.",
  agency: "City of San Diego",
  department: "San Diego Police Department",
  contact: {
    name: "William Eames",
    title: "Supervising Procurement Contracting Officer",
    address: "1200 Third Avenue, Suite 200, San Diego, CA 92101",
    /**
     * The RFI PDF prints (619) 236-6614; the PlanetBids listing for this
     * solicitation prints 619-236-6134. Both are the City's own materials and
     * they disagree. Neither number is printed in our response, so this is
     * recorded for reference only. Use the email if contact is needed.
     */
    phone: "(619) 236-6614",
    phonePerPlanetBids: "(619) 236-6134",
    email: "wbeames@sandiego.gov",
  },
  /** Calls for service per year, stated by SDPD in the RFI background section. */
  callsForServicePerYear: 400000,
  /** Report types SDPD names in RFI §I.A. */
  patrolReportTypes: [
    "Arrest reports",
    "Crime reports",
    "Officer only (ARJIS-9) reports",
    "Traffic collision reports",
    "Death reports",
    "Missing persons reports",
  ],
  investigativeReportTypes: [
    "Follow-up investigations",
    "Homicide investigations",
    "Domestic violence investigations",
    "Child abuse investigations",
    "Search / arrest warrant affidavits",
    "Complaint / internal investigations",
  ],
  /** SDPD's own stated requirement, from RFI §II.B. */
  statedRequirement:
    "The program must assist the officer or investigator in writing their reports while allowing the officer/investigator to maintain complete control over the report's content and structure.",
} as const;

// ===================================================================
// SDPD scale and budget
// Source: City of San Diego, Office of the Independent Budget Analyst,
// IBA Report 26-09, "Analysis of the Police Department's Fiscal Year 2026
// Budget," issued March 27, 2026. Figures from Figure A (budget summary),
// Figure D (budget by section), Figure E (overtime) and Figure G (contracts).
// ===================================================================

export const SDPD_BUDGET_SOURCE =
  "City of San Diego Office of the Independent Budget Analyst, Report 26-09, " +
  "Analysis of the Police Department's Fiscal Year 2026 Budget (March 27, 2026)";

export const SDPD = {
  /**
   * Sworn personnel actually on staff as of February 2026, per SDPD's own
   * "By the Numbers" page. This is the figure to license against: you cannot
   * license a funded vacancy.
   * Source: sandiego.gov/police
   */
  swornActual: 1822,
  /**
   * FY2026 Adopted *budgeted* sworn positions (FY2025 was 2,040.88; 9.88 sworn
   * FTE reduced). Roughly 209 above actual staffing, which is the vacancy gap.
   * Useful for budget context, wrong for headcount-based pricing.
   */
  swornPositions: 2031,
  totalFTE: 2680.46,
  /** FTE in the Operations Section, the units that interact with the public. */
  operationsFTE: 1928.2,
  generalFundBudget: 703_515_478,
  personnelExpense: 614_695_588,
  salaryAndWages: 318_356_069,
  fringeBenefits: 251_075_250,
  overtime: 45_264_269,
  /** Overtime required to appear in court under subpoena. Non-discretionary. */
  courtOvertime: 2_308_199,
  /** SDPD's annual budgeted cost for ARJIS and eSUN network access. */
  arjisAndESunNetworkCosts: 2_015_167,
  /** Budgeted contract line for sign language, translation and transcription. */
  languageServicesContract: 90_000,
  source: SDPD_BUDGET_SOURCE,
  /** Source for swornActual specifically. */
  staffingSource: 'San Diego Police Department, "By the Numbers," as of February 2026',
} as const;

/**
 * Fully-loaded annual cost per budgeted position, derived from the FY2026
 * Adopted Budget: (Salary and Wages + Fringe Benefits) ÷ Total FTE.
 *
 * Overtime is deliberately excluded from this base so it can be modeled
 * separately in the calculator. This average spans sworn and civilian
 * classifications; SDPD can substitute a sworn-only figure in the calculator.
 *
 * Sanity anchor: the City's published sworn pay scale (effective July 1, 2022)
 * put Police Officer II Step C at $46.05/hr base. The figure below is fully
 * loaded (salary + fringe) and three fiscal years later, so a materially
 * higher number is expected.
 */
export const LOADED_COST_PER_POSITION = Math.round(
  (SDPD.salaryAndWages + SDPD.fringeBenefits) / SDPD.totalFTE
); // $212,438

/** 40 hr × 52 wk, the standard US labor-rate denominator. */
export const HOURS_PER_WORK_YEAR = 2080;

/** FLSA: police overtime is paid at 1.5× the regular hourly rate. */
export const OT_MULTIPLIER = 1.5;

// ===================================================================
// San Diego demographics: why multilingual capability is a requirement
// Source: U.S. Census Bureau American Community Survey, 2024 1-year
// estimates, City of San Diego (via Census Reporter).
// ===================================================================

export const SD_LANGUAGE = {
  /** Persons age 5+ speaking a language other than English at home. */
  nonEnglishAtHomePct: 40.9,
  nonEnglishAtHomeCount: 548_723,
  marginOfErrorPct: 1.2,
  source:
    "U.S. Census Bureau, American Community Survey 2024 1-year estimates, City of San Diego",
  /**
   * Ranked non-English languages spoken at home in the City of San Diego.
   * Shares are of total population; Spanish dominates, followed by Tagalog
   * and Vietnamese. Every language listed is supported for transcription.
   */
  topLanguages: [
    { name: "Spanish", sharePct: 28.0, supported: true },
    { name: "Tagalog (Filipino)", sharePct: 2.0, supported: true },
    { name: "Vietnamese", sharePct: 1.0, supported: true },
    { name: "Chinese (Mandarin & Cantonese)", sharePct: 0.9, supported: true },
    { name: "Arabic", sharePct: 0.7, supported: true },
    { name: "Persian (Farsi)", sharePct: 0.4, supported: true },
    { name: "Somali", sharePct: 0.3, supported: true },
  ],
} as const;

/** Total spoken languages supported for transcription. */
export const SUPPORTED_LANGUAGE_COUNT = 56;

// ===================================================================
// California Penal Code § 13663 (added by SB 524, ch. 587)
// Signed October 10, 2025. Effective January 1, 2026.
// ===================================================================

export const PC_13663 = {
  billName: "SB 524",
  codeSection: "California Penal Code § 13663",
  signedDate: "October 10, 2025",
  effectiveDate: "January 1, 2026",
  elements: [
    {
      cite: "§ 13663(a)",
      requirement: "AI-use disclosure",
      detail:
        "A report drafted with AI must state on each page, or within the body of the report, that it was written either fully or in part using artificial intelligence, and must identify every specific AI program used.",
    },
    {
      cite: "§ 13663(a)(2)",
      requirement: "Officer certification",
      detail:
        "The officer who prepared the report must sign it, verifying that they reviewed its contents and that the facts contained in the report are true and correct.",
    },
    {
      cite: "§ 13663(b)(1)",
      requirement: "First-draft retention",
      detail:
        "The agency must retain the first draft created for as long as the official report is maintained.",
    },
    {
      cite: "§ 13663(c)",
      requirement: "Audit trail",
      detail:
        "The agency must maintain an audit trail identifying the person who used AI to create the report and the video and audio footage used.",
    },
    {
      cite: "§ 13663(d)(1)",
      requirement: "Vendor data-use restriction",
      detail:
        "A contracted vendor may not share, sell, or otherwise use information provided by the law enforcement agency except for the contracting agency's purposes or pursuant to a court order.",
    },
    {
      cite: "§ 13663(d)(2)",
      requirement: "Permitted vendor access",
      detail:
        "A vendor may access agency data for troubleshooting, bias mitigation, accuracy improvement, or system refinement.",
    },
    {
      cite: "§ 13663(e)(1)",
      requirement: "Scope",
      detail:
        "The statute covers systems that automatically draft report narratives from body-worn or vehicle camera recordings, and systems that analyze an officer's dictated report to generate or enhance a narrative using generative AI.",
    },
  ],
} as const;

// ===================================================================
// SDPD's existing Axon footprint
// Source: SDPD Surveillance Impact Report and Surveillance Use Policy for
// Axon Body Worn Camera and Evidence.com, City of San Diego.
// ===================================================================

export const SDPD_AXON = {
  bodyCamerasInService: 1500,
  evidencePlatform: "sdpd.evidence.com",
  source:
    "SDPD Surveillance Impact Report: Axon Body Worn Camera and Evidence.com (City of San Diego)",
} as const;

// ===================================================================
// Time-savings model
// Constants are the deployed production values in
// server/src/services/trialFindingsSnapshotService.ts (lines 31-34).
// Calibrated so a ~360-word report yields ~45 minutes of manual time.
// ===================================================================

export const MODEL = {
  /** Fixed per-report overhead: pulling up the case, running subjects,
   *  gathering evidence, navigating between RMS and editor, applying agency
   *  formatting, sign-off. */
  setupMinPerReport: 30,
  /** Sustained composition speed for report prose, not raw typing speed. */
  typingWpm: 30,
  /** Officer review time for an AI-assisted report. */
  reviewMinPerCase: 5,
  /** Playback speed when an officer re-listens to interview / body-cam audio. */
  relistenSpeed: 1.5,
} as const;

/**
 * Manual minutes to produce one report.
 *
 * Note that narrative dictation audio is deliberately NOT in this formula.
 * Dictation replaces typing; it is not additional manual work. Only
 * interaction audio (interviews, body-worn footage) adds re-listening time.
 */
export function manualMinutes(words: number, interactionAudioMin: number): number {
  return (
    MODEL.setupMinPerReport +
    words / MODEL.typingWpm +
    interactionAudioMin / MODEL.relistenSpeed
  );
}

export function aiMinutes(): number {
  return MODEL.reviewMinPerCase;
}

/** What the model deliberately leaves out, rendered on the page rather than buried. */
export const MODEL_EXCLUSIONS = [
  "Supervisor review and approval time",
  "Reduced end-of-shift overtime",
  "Faster case turnaround to the District Attorney",
  "Reports completed in the field rather than at a station",
  "Time recovered when a report is not kicked back for missing elements",
] as const;

// ===================================================================
// Production usage
//
// Each row covers that agency's ENTIRE history through the snapshot date
// rather than a trial window, which is the point of the section: sustained
// use across a year answers a question a three-month pilot cannot.
//
// Status differs and the copy must not flatten it. Greeley and Manchester
// are paying departments in daily use since mid-2025. Anchorage ran an
// eleven-month evaluation with a small group of officers and converted to
// a department purchase in August 2026, which is the whole reason its
// officer count is the smallest here. Do not describe all three as
// paying customers for the full period.
//
// Source: read-only aggregate query against the production database on
// 2026-08-17, applying the model constants above. Audio duration is
// estimated from file size because case_files.metadata.duration is not
// populated on any row; this is the same estimator the TrialFindings
// pages and the Manchester study use, so every published figure stays on
// a single methodology.
// ===================================================================

export type Deployment = {
  agency: string;
  location: string;
  /**
   * Department sworn strength. Shown for context, to establish that these are
   * real agencies of consequential size rather than small pilots. It is NOT a
   * denominator: dividing officersUsing by it would manufacture a fake
   * adoption rate out of what was really a purchasing decision.
   */
  sworn: number;
  /**
   * Seats the department actually purchased, which tracks sworn strength
   * poorly in both directions: Greeley bought 190 against 168 sworn, while
   * Anchorage bought 138 against 450. Older contracts did not require
   * department-wide enrollment, so the size of a buy says nothing about how
   * well the platform worked for the officers who had it.
   */
  seats: number;
  /**
   * Officers who wrote at least one report. This is the limited pool every
   * time figure below is computed from: officers who never had access
   * contribute no reports and therefore no minutes, in either direction.
   */
  officersUsing: number;
  reports: number;
  aiAssistedReports: number;
  words: number;
  avgWords: number;
  medianWords: number;
  narrativeAudioHr: number;
  interactionAudioHr: number;
  manualHr: number;
  aiHr: number;
  savedHr: number;
  minSavedPerReport: number;
  windowLabel: string;
};

export const DEPLOYMENTS: Deployment[] = [
  {
    agency: "Greeley Police Department",
    location: "Greeley, Colorado",
    sworn: 168,
    seats: 190,
    officersUsing: 117,
    reports: 9_230,
    aiAssistedReports: 7_239,
    words: 4_315_344,
    avgWords: 596.1,
    medianWords: 490,
    narrativeAudioHr: 1_341.4,
    interactionAudioHr: 881.5,
    manualHr: 6_604.6,
    aiHr: 603.3,
    savedHr: 6_001.3,
    minSavedPerReport: 49.7,
    windowLabel: "Jun 2025 to present",
  },
  {
    agency: "Manchester Police Department",
    location: "Manchester, New Hampshire",
    sworn: 250,
    seats: 175,
    officersUsing: 134,
    reports: 2_297,
    aiAssistedReports: 2_145,
    words: 1_541_851,
    avgWords: 718.8,
    medianWords: 657,
    narrativeAudioHr: 215.2,
    interactionAudioHr: 1_381.8,
    manualHr: 2_850.3,
    aiHr: 178.8,
    savedHr: 2_671.6,
    minSavedPerReport: 74.7,
    windowLabel: "Jul 2025 to present",
  },
  {
    agency: "Anchorage Police Department",
    location: "Anchorage, Alaska",
    sworn: 450,
    /**
     * 138 seats purchased in August 2026, at the end of an eleven-month
     * evaluation run with roughly 15 to 20 officers at a time. The 28
     * officers below are everyone who wrote a report across that whole
     * evaluation, not a measure of uptake against either the purchase or
     * the department's 450 sworn.
     */
    seats: 138,
    officersUsing: 28,
    reports: 1_176,
    aiAssistedReports: 1_164,
    words: 811_391,
    avgWords: 697.1,
    medianWords: 644,
    narrativeAudioHr: 204.2,
    interactionAudioHr: 457.7,
    manualHr: 1_337.9,
    aiHr: 97.0,
    savedHr: 1_240.9,
    minSavedPerReport: 64.0,
    windowLabel: "Sep 2025 to present",
  },
];

// ===================================================================
// Mandated periodic reporting California law already requires of SDPD.
//
// These are the obligations the structured-extraction proposal in the
// "Built for San Diego" section is aimed at. Each citation was verified
// against the statute; do not paraphrase the required fields loosely,
// because a procurement reader will check them.
// ===================================================================

export type MandatedReport = {
  cite: string;
  name: string;
  cadence: string;
  requires: string[];
  goesTo: string;
};

export const MANDATED_REPORTS: MandatedReport[] = [
  {
    cite: "Gov. Code § 12525.2",
    name: "Use of force incident reporting (URSUS)",
    cadence: "Monthly, summarized annually by DOJ",
    requires: [
      "Gender, race, and age of each person shot, injured, or killed",
      "Type of force used, including the types of weapons used",
      "Number of officers and civilians involved in the incident",
    ],
    goesTo: "California Department of Justice",
  },
  {
    cite: "Gov. Code § 12525.5",
    name: "Racial and Identity Profiling Act stop data (AB 953)",
    cadence: "Annual, reporting period closes April 1",
    requires: [
      "Officer-perceived race or ethnicity, gender, LGBTQ+ status, English fluency, disability, and approximate age",
      "Reason for the stop and actions taken during it",
      "Result of the stop",
    ],
    goesTo: "California Department of Justice OpenJustice portal",
  },
  {
    cite: "AB 481, Gov. Code § 7072",
    name: "Military equipment use report",
    cadence: "Annual, published publicly",
    requires: [
      "Summary of how each type of equipment was used and the purpose of its use",
      "Summary of any complaints or concerns received",
      "Results of internal audits, any policy violations, and actions taken",
      "Total annual cost per equipment type",
    ],
    goesTo: "City of San Diego governing body, published on the department website",
  },
];

// ===================================================================
// The Manchester randomized controlled trial of Axon Draft One.
//
// ACCURACY NOTE: read the paper before editing any of this. The authors give
// two explanations for the null result: (a) agencies already use templates, so
// fill-in-and-confirm is not much different, and (b) the narrative is only one
// part of a report, most of which is data entry. They do NOT find that officers
// spent longer editing drafts to sound like themselves. Do not attribute that
// to the study. Our own argument about authorship is ours and is labeled as
// such wherever it appears.
// ===================================================================

export const DRAFT_ONE_RCT = {
  title:
    "No Man's Hand: Artificial Intelligence Does Not Improve Police Report Writing Speed",
  venue: "CrimRxiv",
  url: "https://www.crimrxiv.com/pub/nxbmzp2j/release/1",
  irb: "University of South Carolina IRB Pro00136198",
  productTested: "Axon Draft One",
  agency: "Manchester Police Department, Manchester, New Hampshire",
  design: "Pre-registered randomized controlled trial",
  officers: 85,
  control: 43,
  treatment: 42,
  reports: 755,
  didReports: 6084,
  /** Axon's own marketing claim, quoted in the paper. */
  vendorClaimPct: 82,
  /** Main model coefficient on the treatment term. Not significant. */
  coefficient: -29.66,
  standardError: 39.62,
  /**
   * The authors' stated explanations, paraphrased closely.
   *
   * Deliberately NOT rendered. Both explanations cut against narrative
   * assistance generally rather than against Axon's approach specifically,
   * so reproducing them argues the reader out of the section. The block that
   * used to display them now carries our own explanation instead, labeled as
   * ours. Retained here because the paraphrase is verified against the paper
   * and we may want it back if a reviewer asks what the authors concluded.
   */
  authorsExplanations: [
    "Many officers and agencies already use templates or boilerplate for common calls, so a fill-in-and-confirm workflow may not differ much from what they were already doing.",
    "Report duration is not narrative duration. Officers also complete extensive data entry: an entry for every complainant, victim, witness, and suspect, plus all evidence and property. Narrative assistance does not touch most of that work.",
  ],
} as const;

// ===================================================================
// Pricing
//
// One rate, one formula. The unit of sale is the department license, not
// the seat: quoting per seat is what invites a department to buy a
// division's worth and call it a deployment. The rate below is how the
// license is sized, not a menu item.
// ===================================================================

export const PRICING = {
  /** Annual license rate per enrolled sworn officer. Command staff optional. */
  perOfficerPerYear: 360,
  /** Where a procurement cycle requires monthly billing. */
  monthlyPerOfficerPerMonth: 40,
} as const;

/**
 * Illustrative license cost at SDPD's actual sworn staffing. Deliberately uses
 * swornActual rather than swornPositions: the license covers officers, not
 * funded vacancies.
 */
export const SDPD_LICENSE_AT_FULL_SWORN =
  SDPD.swornActual * PRICING.perOfficerPerYear;

// ===================================================================
// Investigations: jail call intelligence add-on.
//
// Platform facts trace to the codebase: lead categories are the
// LeadCategory enum in server/src/db/schema.ts; the per-investigator
// price is INVESTIGATIONS_SEAT_PRICE_USD (default 150) in
// server/src/routes/agencyRouter.ts; language handling is
// INVESTIGATION_LANGUAGE_OPTIONS in
// server/src/services/investigations/dispatcher.ts.
//
// The review-time model is ours and is labeled as such on the page.
// ===================================================================

export const INVESTIGATIONS = {
  /** Per investigator, per month, licensed only for the detectives who use it. */
  seatPricePerMonth: 150,
  /** Human-readable labels for the enumerated lead categories the model flags. */
  leadCategories: [
    "Threats of violence",
    "Witness tampering",
    "Three-way calls",
    "Drug transactions",
    "Contraband",
    "Money movement",
    "Coded language",
    "Admissions",
    "Flight risk",
  ],
} as const;

/**
 * What jail-call intelligence costs from the incumbent market. The dominant
 * vendor model is facility-wide monitoring sold as a service; these are the
 * published figures for the largest provider, LEO Technologies (Verus).
 * Each figure carries its press citation; quote them closely.
 */
export const JAIL_CALL_MARKET = {
  /**
   * "The system's cost can vary, LEO Technologies officials said, but
   * generally cost between $500,000 and $600,000 a year for a corrections
   * facility housing about 1,000 inmates."
   * Source: ABC News, "US prisons and jails using AI to mass-monitor
   * millions of inmate calls," October 24, 2019.
   */
  verusFacilityLow: 500_000,
  verusFacilityHigh: 600_000,
  verusFacilityInmates: 1000,
  /**
   * Florida DOC paid $2.5M for up to 50 million minutes of monitored
   * conversations over roughly one year (contract through June 30 of the
   * following year). Five cents a minute at full utilization.
   * Source: Tampa Bay Times, "Florida prisons use artificial intelligence
   * to surveil calls," September 19, 2023.
   */
  floridaContract: 2_500_000,
  floridaMinutes: 50_000_000,
} as const;

export const JAIL_CALL_MARKET_SOURCES =
  'ABC News, "US prisons and jails using AI to mass-monitor millions of inmate calls" (Oct. 24, 2019); ' +
  'Tampa Bay Times, "Florida prisons use artificial intelligence to surveil calls" (Sept. 19, 2023)';

/**
 * Review-time model for a batch of jail calls.
 *
 * Manual review is audio duration divided by MODEL.relistenSpeed, the same
 * 1.5x playback constant the Section 07 model uses. That is deliberately
 * generous to the manual side: first-pass review of unfamiliar,
 * phone-quality audio with note-taking typically runs at or below real
 * time, not above it.
 *
 * With the platform, the detective reads the brief and leads queue
 * (briefReviewMin) and verifies a share of the audio by ear
 * (verifySharePct). Both are our assumptions and both are editable on the
 * page.
 */
export const JAIL_CALL_MODEL = {
  callsPerBatch: 50,
  avgCallMin: 15,
  reviewSpeed: MODEL.relistenSpeed,
  briefReviewMin: 20,
  verifySharePct: 10,
} as const;

// ===================================================================
// Report scoring bands. Source: client/src/components/Editor/ReportScore.tsx
// ===================================================================

export const SCORE_BANDS = [
  { min: 90, label: "Outstanding", color: "#10b981" },
  { min: 80, label: "Excellent", color: "#22c55e" },
  { min: 65, label: "Satisfactory", color: "#f59e0b" },
  { min: 30, label: "Needs more detail", color: "#f97316" },
  { min: 0, label: "Insufficient information", color: "#ef4444" },
] as const;

// ===================================================================
// Redaction categories detected automatically, with the California
// statutory basis most commonly cited for withholding.
// ===================================================================

export const REDACTION_CATEGORIES = [
  "Name",
  "Address",
  "Phone",
  "Email",
  "Social Security Number",
  "Date of Birth",
  "License Plate",
  "Driver's License",
  "Vehicle",
  "Juvenile",
  "Medical",
  "Financial",
  "Other",
] as const;
